/**
 * 設定頁「帳號／私隱／匯出／關於」段（設計 v2 S7 後半；UI v2 **步 9**）
 *
 *   ⚠️ 由「我的」（`MePage`）**搬過嚟**（設計 §11 步 9：SettingsPage 吸收 MePage 帳號部分）
 *   ⚠️ **唔准假成功**：額度照實讀；匯出格式**未定**（設計 R-15）→ 掣暫時停用、照實講
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getDeviceId } from '../../hooks/useChatStream';
import BrandMark from '../../components/BrandMark';
import { capabilitiesFromBrowser, formatCapabilities } from '../../shareProbe';
import { currentBundleName } from './appInfo';

export default function InfoSections() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [deviceId] = useState(() => getDeviceId());
  const [bundleName] = useState(() => currentBundleName());
  const [shareCap] = useState(() => formatCapabilities(capabilitiesFromBrowser()));

  return (
    <>
      {/* 帳號同額度（由「我的」搬過嚟） */}
      <section className="settings__group" data-testid="me-identity">
        <h2 className="section-title">{t('settings_account')}</h2>
        <p style={{ margin: '0 0 6px', wordBreak: 'break-all' }} data-testid="me-account-email">
          {t('me_logged_in_as')}：<strong>{auth.user?.email ?? '—'}</strong>
        </p>
        <p style={{ margin: '0 0 6px' }} data-testid="me-credits-left">
          {t('me_credits_left', { n: auth.credits })}
        </p>
        <div className="settings__row" style={{ gap: 'var(--space-2)', justifyContent: 'flex-start' }}>
          <button type="button" className="btn" onClick={() => navigate('/me')}>
            {t('me_topup_cta')}
          </button>
          <button
            type="button"
            data-testid="me-logout"
            className="btn"
            onClick={() => void auth.logout()}
          >
            {t('me_logout')}
          </button>
        </div>
        {/* 舊「裝置身份」仍然照實列出（客服對問題時有用；唔再係身份） */}
        <p className="footnote" style={{ margin: '8px 0 0', wordBreak: 'break-all' }}>
          {t('me_identity_id')}：{deviceId}
        </p>
      </section>

      {/* 私隱同匿名 */}
      <section className="settings__group" data-testid="settings-privacy">
        <h2 className="section-title">{t('settings_privacy')}</h2>
        <p className="footnote" style={{ margin: 0 }}>
          {t('privacy_anon_note')}
        </p>
      </section>

      {/* 匯出日記（⚠️ R-15：格式未定 → 停用＋照實講） */}
      <section className="settings__group" data-testid="settings-export">
        <h2 className="section-title">{t('settings_export')}</h2>
        <p className="footnote" style={{ margin: '0 0 var(--space-2)' }}>
          {t('export_hint')}
        </p>
        <button type="button" className="btn" data-testid="export-button" disabled aria-disabled="true">
          {t('export_button')}
        </button>
      </section>

      {/* 關於 */}
      <section className="settings__group" data-testid="me-about">
        <h2 className="section-title">{t('me_about_title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 4px' }}>
          <BrandMark size={40} field />
          <p className="footnote" style={{ margin: 0 }}>
            {t('app_name')} · {t('me_version')}
          </p>
        </div>
        <p className="footnote" style={{ margin: '4px 0 0' }} data-testid="me-build">
          {t('me_build_label')}：<code>{bundleName || t('me_build_unknown')}</code>
        </p>
        <p className="footnote" style={{ margin: '4px 0 0' }} data-testid="me-share-probe">
          {t('me_share_probe_label')}：<code>{shareCap}</code>
        </p>
        <p className="footnote" style={{ margin: '2px 0 0' }}>{t('me_share_probe_legend')}</p>
      </section>
    </>
  );
}
